/**
 * Error Notifications Component
 *
 * Displays real-time error notifications from containers.
 * Features:
 * - Toast-style notifications with auto-dismiss
 * - Severity-based coloring (info, warning, error, critical)
 * - Error list view with pagination
 * - Resolve/acknowledge actions
 * - WebSocket real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { useWebSocket } from '../../hooks/useWebSocket';

// =============================================================================
// Types
// =============================================================================

export interface ContainerError {
  id: string;
  nodeId: string;
  containerId?: string;
  containerName?: string;
  type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  details?: Record<string, unknown>;
  logs?: string;
  exitCode?: number;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  occurredAt: string;
  createdAt: string;
}

export interface ErrorEvent {
  type: 'container:error';
  nodeId: string;
  containerId?: string;
  data: ContainerError;
}

interface ErrorsResponse {
  errors: ContainerError[];
  total: number;
}

interface ErrorStatsResponse {
  total: number;
  unresolved: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get severity color classes
 */
function getSeverityColors(severity: ContainerError['severity']): {
  bg: string;
  border: string;
  text: string;
  icon: string;
} {
  switch (severity) {
    case 'info':
      return {
        bg: 'bg-blue-900/20',
        border: 'border-blue-500/50',
        text: 'text-blue-300',
        icon: 'text-blue-400',
      };
    case 'warning':
      return {
        bg: 'bg-yellow-900/20',
        border: 'border-yellow-500/50',
        text: 'text-yellow-300',
        icon: 'text-yellow-400',
      };
    case 'error':
      return {
        bg: 'bg-red-900/20',
        border: 'border-red-500/50',
        text: 'text-red-300',
        icon: 'text-red-400',
      };
    case 'critical':
      return {
        bg: 'bg-red-900/40',
        border: 'border-red-500',
        text: 'text-red-200',
        icon: 'text-red-300',
      };
    default:
      return {
        bg: 'bg-gray-900/20',
        border: 'border-gray-500/50',
        text: 'text-gray-300',
        icon: 'text-gray-400',
      };
  }
}

/**
 * Get error type label
 */
function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    container_exit: 'Container Exit',
    container_oom: 'Out of Memory',
    gpu_error: 'GPU Error',
    model_error: 'Model Error',
    network_error: 'Network Error',
    config_error: 'Config Error',
    startup_error: 'Startup Error',
    unknown: 'Unknown Error',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// =============================================================================
// Icons
// =============================================================================

function InfoIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CriticalIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function getSeverityIcon(severity: ContainerError['severity']) {
  switch (severity) {
    case 'info':
      return <InfoIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'critical':
      return <CriticalIcon />;
    default:
      return <ErrorIcon />;
  }
}

// =============================================================================
// Toast Component
// =============================================================================

interface ToastProps {
  error: ContainerError;
  onDismiss: () => void;
  onResolve: () => void;
  nodeName?: string;
}

function Toast({ error, onDismiss, onResolve, nodeName }: ToastProps) {
  const colors = getSeverityColors(error.severity);

  return (
    <div
      className={`rounded-lg border ${colors.border} ${colors.bg} p-4 shadow-lg animate-slide-in`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${colors.icon}`}>
          {getSeverityIcon(error.severity)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${colors.text}`}>
              {getTypeLabel(error.type)}
            </span>
            {error.severity === 'critical' && (
              <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-500 text-white uppercase animate-pulse">
                Critical
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-300 line-clamp-2">{error.message}</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <span>{error.containerName || error.containerId || 'Unknown'}</span>
            {nodeName && (
              <>
                <span>-</span>
                <span>{nodeName}</span>
              </>
            )}
            <span>-</span>
            <span>{formatTimestamp(error.occurredAt)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onResolve}
            className="rounded p-1 text-gray-400 hover:text-green-400 hover:bg-green-900/20 transition-colors"
            title="Mark as resolved"
          >
            <CheckIcon />
          </button>
          <button
            onClick={onDismiss}
            className="rounded p-1 text-gray-400 hover:text-white hover:bg-discord-light transition-colors"
            title="Dismiss"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Error List Item Component
// =============================================================================

interface ErrorListItemProps {
  error: ContainerError;
  onResolve: (id: string) => void;
  onExpand: (id: string) => void;
  isExpanded: boolean;
  nodeName?: string;
}

function ErrorListItem({
  error,
  onResolve,
  onExpand,
  isExpanded,
  nodeName,
}: ErrorListItemProps) {
  const colors = getSeverityColors(error.severity);

  return (
    <div
      className={`rounded-lg border ${colors.border} ${colors.bg} overflow-hidden transition-all`}
    >
      {/* Header */}
      <button
        onClick={() => onExpand(error.id)}
        className="w-full p-3 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
            {getSeverityIcon(error.severity)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${colors.text}`}>
                {getTypeLabel(error.type)}
              </span>
              {error.resolved && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-400">
                  Resolved
                </span>
              )}
            </div>
            <p className="mt-0.5 text-sm text-gray-300 truncate">{error.message}</p>
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
              <span>{error.containerName || error.containerId || 'System'}</span>
              {nodeName && (
                <>
                  <span>-</span>
                  <span>{nodeName}</span>
                </>
              )}
              <span>-</span>
              <span>{formatTimestamp(error.occurredAt)}</span>
            </div>
          </div>

          {/* Expand icon */}
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-discord-light/30">
          {/* Full message */}
          <div className="mt-3">
            <h4 className="text-xs font-medium text-gray-400 mb-1">Message</h4>
            <p className="text-sm text-gray-300">{error.message}</p>
          </div>

          {/* Details */}
          {error.details && Object.keys(error.details).length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-400 mb-1">Details</h4>
              <pre className="text-xs text-gray-400 bg-discord-darker p-2 rounded overflow-x-auto">
                {JSON.stringify(error.details, null, 2)}
              </pre>
            </div>
          )}

          {/* Logs */}
          {error.logs && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-400 mb-1">Logs</h4>
              <pre className="text-xs text-gray-400 bg-discord-darker p-2 rounded max-h-32 overflow-auto font-mono">
                {error.logs}
              </pre>
            </div>
          )}

          {/* Exit code */}
          {error.exitCode !== undefined && error.exitCode !== null && (
            <div className="mt-3">
              <span className="text-xs text-gray-400">Exit code: </span>
              <span className="text-xs text-red-400 font-mono">{error.exitCode}</span>
            </div>
          )}

          {/* Actions */}
          {!error.resolved && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => onResolve(error.id)}
                className="inline-flex items-center gap-1 rounded-md bg-green-900/50 px-3 py-1.5 text-xs font-medium text-green-300 hover:bg-green-900 transition-colors"
              >
                <CheckIcon />
                Mark Resolved
              </button>
            </div>
          )}

          {/* Resolution info */}
          {error.resolved && error.resolvedAt && (
            <div className="mt-3 text-xs text-gray-500">
              Resolved {formatTimestamp(error.resolvedAt)}
              {error.resolvedBy && ` by ${error.resolvedBy}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Toast Container Component
// =============================================================================

interface ToastContainerProps {
  maxToasts?: number;
  autoDismissMs?: number;
  nodeNames?: Map<string, string>;
}

/**
 * Toast container for real-time error notifications
 *
 * Displays toast notifications for new errors with auto-dismiss.
 */
export function ErrorToastContainer({
  maxToasts = 5,
  autoDismissMs = 10000,
  nodeNames = new Map(),
}: ToastContainerProps) {
  const { accessToken } = useAuth();
  const [toasts, setToasts] = useState<ContainerError[]>([]);
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const { subscribe } = useWebSocket({
    accessToken,
    autoConnect: true,
  });

  /**
   * Dismiss a toast
   */
  const dismissToast = useCallback((id: string) => {
    // Clear timer if exists
    const timer = dismissTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimersRef.current.delete(id);
    }

    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * Resolve an error
   */
  const resolveError = useCallback(
    async (id: string) => {
      const apiClient = getApiClient();

      // Find the error to get nodeId
      const error = toasts.find((t) => t.id === id);
      if (!error) return;

      try {
        await apiClient.post(`/nodes/${error.nodeId}/errors/${id}/resolve`);
        dismissToast(id);
      } catch {
        // Silently fail - error is still visible
      }
    },
    [toasts, dismissToast]
  );

  /**
   * Add a new toast
   */
  const addToast = useCallback(
    (error: ContainerError) => {
      setToasts((prev) => {
        // Don't add duplicates
        if (prev.some((t) => t.id === error.id)) {
          return prev;
        }

        // Add new toast at the beginning
        const updated = [error, ...prev];

        // Remove oldest if over limit
        if (updated.length > maxToasts) {
          const removed = updated.pop();
          if (removed) {
            const timer = dismissTimersRef.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              dismissTimersRef.current.delete(removed.id);
            }
          }
        }

        return updated;
      });

      // Set auto-dismiss timer
      if (autoDismissMs > 0) {
        const timer = setTimeout(() => {
          dismissToast(error.id);
        }, autoDismissMs);
        dismissTimersRef.current.set(error.id, timer);
      }
    },
    [maxToasts, autoDismissMs, dismissToast]
  );

  /**
   * Subscribe to error events
   */
  useEffect(() => {
    const unsubscribe = subscribe<ContainerError>('container:error', (error) => {
      // Only show toasts for unresolved errors
      if (!error.resolved) {
        addToast(error);
      }
    });
    return unsubscribe;
  }, [subscribe, addToast]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      dismissTimersRef.current.forEach((timer) => clearTimeout(timer));
      dismissTimersRef.current.clear();
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full">
      {toasts.map((error) => (
        <Toast
          key={error.id}
          error={error}
          onDismiss={() => dismissToast(error.id)}
          onResolve={() => void resolveError(error.id)}
          nodeName={nodeNames.get(error.nodeId)}
        />
      ))}
    </div>
  );
}

// =============================================================================
// Error List Component
// =============================================================================

export interface ErrorListProps {
  /** Filter by specific node ID */
  nodeId?: string;
  /** Show only specific severities */
  severities?: ContainerError['severity'][];
  /** Show resolved errors */
  showResolved?: boolean;
  /** Maximum items per page */
  pageSize?: number;
  /** Node names map for display */
  nodeNames?: Map<string, string>;
}

/**
 * Error List component
 *
 * Displays a paginated list of container errors with expand/collapse details.
 */
export function ErrorList({
  nodeId,
  severities,
  showResolved = false,
  pageSize = 10,
  nodeNames = new Map(),
}: ErrorListProps) {
  const { accessToken } = useAuth();
  const [errors, setErrors] = useState<ContainerError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { subscribe, isConnected } = useWebSocket({
    accessToken,
    autoConnect: true,
  });

  /**
   * Fetch errors from API
   */
  const fetchErrors = useCallback(async () => {
    const apiClient = getApiClient();
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      if (!showResolved) params.set('resolved', 'false');
      if (severities?.length) params.set('severity', severities.join(','));

      const endpoint = nodeId
        ? `/nodes/${nodeId}/errors?${params.toString()}`
        : `/nodes/dashboard/errors?${params.toString()}`;

      const result = await apiClient.get<ErrorsResponse>(endpoint);

      if (isSuccess(result)) {
        setErrors(result.data.errors || []);
        setTotal(result.data.total || 0);
      } else {
        setError(result.error.message || 'Failed to fetch errors');
      }
    } catch {
      setError('Failed to load error data');
    } finally {
      setIsLoading(false);
    }
  }, [nodeId, page, pageSize, showResolved, severities]);

  /**
   * Resolve an error
   */
  const resolveError = useCallback(
    async (errorId: string) => {
      const apiClient = getApiClient();
      const errorItem = errors.find((e) => e.id === errorId);
      if (!errorItem) return;

      try {
        const result = await apiClient.post(
          `/nodes/${errorItem.nodeId}/errors/${errorId}/resolve`
        );

        if (isSuccess(result)) {
          // Update local state
          setErrors((prev) =>
            prev.map((e) =>
              e.id === errorId
                ? { ...e, resolved: true, resolvedAt: new Date().toISOString() }
                : e
            )
          );
        }
      } catch {
        // Silently fail
      }
    },
    [errors]
  );

  /**
   * Initial fetch
   */
  useEffect(() => {
    void fetchErrors();
  }, [fetchErrors]);

  /**
   * Subscribe to error events
   */
  useEffect(() => {
    const unsubscribe = subscribe<ContainerError>('container:error', (newError) => {
      // Filter by nodeId if specified
      if (nodeId && newError.nodeId !== nodeId) return;

      // Add to list if on first page
      if (page === 1) {
        setErrors((prev) => {
          // Don't add if already exists
          if (prev.some((e) => e.id === newError.id)) return prev;
          // Add at beginning, remove last if over limit
          const updated = [newError, ...prev];
          if (updated.length > pageSize) updated.pop();
          return updated;
        });
        setTotal((prev) => prev + 1);
      }
    });
    return unsubscribe;
  }, [subscribe, nodeId, page, pageSize]);

  const totalPages = Math.ceil(total / pageSize);

  // Loading state
  if (isLoading && errors.length === 0) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-3">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 bg-discord-light rounded" />
              <div className="flex-1">
                <div className="h-4 w-1/3 bg-discord-light rounded mb-2" />
                <div className="h-3 w-2/3 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && errors.length === 0) {
    return (
      <div className="rounded-lg bg-red-900/20 border border-red-500/50 p-4">
        <p className="text-sm text-red-300">{error}</p>
        <button
          onClick={() => void fetchErrors()}
          className="mt-2 text-xs text-red-400 hover:text-red-300"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (errors.length === 0) {
    return (
      <div className="text-center py-8">
        <svg
          className="mx-auto h-10 w-10 text-gray-500 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-sm text-gray-400">No errors to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Connection indicator */}
      {isConnected && (
        <div className="flex items-center gap-1 text-xs text-green-400">
          <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
          Live updates
        </div>
      )}

      {/* Error list */}
      <div className="space-y-2">
        {errors.map((err) => (
          <ErrorListItem
            key={err.id}
            error={err}
            onResolve={resolveError}
            onExpand={setExpandedId}
            isExpanded={expandedId === err.id}
            nodeName={nodeNames.get(err.nodeId)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-discord-light">
          <span className="text-xs text-gray-500">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-discord-light disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-500">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-discord-light disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Error Stats Component
// =============================================================================

export interface ErrorStatsProps {
  nodeId?: string;
}

/**
 * Error Statistics component
 *
 * Displays summary statistics for errors.
 */
export function ErrorStats({ nodeId }: ErrorStatsProps) {
  const [stats, setStats] = useState<ErrorStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const apiClient = getApiClient();
      setIsLoading(true);

      try {
        const endpoint = nodeId
          ? `/nodes/${nodeId}/errors/stats`
          : '/nodes/dashboard/errors/stats';

        const result = await apiClient.get<ErrorStatsResponse>(endpoint);

        if (isSuccess(result)) {
          setStats(result.data);
        }
      } catch {
        // Silently fail
      } finally {
        setIsLoading(false);
      }
    };

    void fetchStats();
  }, [nodeId]);

  if (isLoading || !stats) {
    return (
      <div className="flex items-center gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-6 w-12 bg-discord-light rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 text-sm">
      <div className="flex items-center gap-1">
        <span className="font-medium text-white">{stats.total}</span>
        <span className="text-gray-500">total</span>
      </div>
      {stats.unresolved > 0 && (
        <div className="flex items-center gap-1">
          <span className="font-medium text-red-400">{stats.unresolved}</span>
          <span className="text-gray-500">unresolved</span>
        </div>
      )}
      {stats.bySeverity['critical'] > 0 && (
        <div className="flex items-center gap-1">
          <span className="font-medium text-red-300">{stats.bySeverity['critical']}</span>
          <span className="text-gray-500">critical</span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CSS for animations (add to global styles)
// =============================================================================

// Add to your global CSS:
// @keyframes slide-in {
//   from {
//     transform: translateX(100%);
//     opacity: 0;
//   }
//   to {
//     transform: translateX(0);
//     opacity: 1;
//   }
// }
// .animate-slide-in {
//   animation: slide-in 0.3s ease-out;
// }

export default ErrorList;
